import { useEffect, useRef, useState } from "react";

const DEFAULT_MAX_LENGTH = 32;
const DEBOUNCE_MS = 300;

const computeChecks = (password, maxLength) => ({
  length:
    password.length > 7 &&
    (maxLength == null || password.length <= maxLength),
  lower: /[a-z]/.test(password),
  upper: /[A-Z]/.test(password),
  digit: /[0-9]/.test(password),
  special: /[!@#$%^&+=]+/.test(password),
});

// Fixed-order list of all five requirements with their individual pass/fail
// state. The widget renders this verbatim - a satisfied requirement stays
// visible (now green with a ✓) instead of disappearing, so the user keeps
// seeing the full checklist as they type.
const buildRequirements = (checks, lengthMessage) => [
  { key: "lower", text: "Przynajmniej jedną małą literę", met: checks.lower },
  { key: "upper", text: "Przynajmniej jedną wielką literę", met: checks.upper },
  {
    key: "special",
    text: "Przynajmniej jeden znak specjalny: ! @ # $ % ^ & + =",
    met: checks.special,
  },
  { key: "digit", text: "Przynajmniej jedną cyfrę", met: checks.digit },
  { key: "length", text: lengthMessage, met: checks.length },
];

const buildMissingElements = (requirements) =>
  requirements.filter((r) => !r.met).map((r) => r.text);

export const usePasswordStrength = (
  password,
  { maxLength = DEFAULT_MAX_LENGTH } = {}
) => {
  const lengthMessage =
    maxLength == null
      ? "Długość co najmniej 8 znaków"
      : `Długość znaków między 8, a ${maxLength}.`;

  const initialChecks = computeChecks("", maxLength);
  const initialRequirements = buildRequirements(initialChecks, lengthMessage);
  const [checks, setChecks] = useState(initialChecks);
  const [score, setScore] = useState(0);
  const [requirements, setRequirements] = useState(initialRequirements);
  const [missingElements, setMissingElements] = useState(
    buildMissingElements(initialRequirements)
  );
  const timerRef = useRef(null);

  useEffect(() => {
    const nextChecks = computeChecks(password, maxLength);
    const nextScore = Object.values(nextChecks).filter(Boolean).length;
    const nextRequirements = buildRequirements(nextChecks, lengthMessage);
    const nextMissing = buildMissingElements(nextRequirements);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setChecks(nextChecks);
      setScore(nextScore);
      setRequirements(nextRequirements);
      setMissingElements(nextMissing);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [password, maxLength, lengthMessage]);

  return { score, checks, requirements, missingElements };
};
